import EntityMultiSelect, { selectedEntityIds } from '../../../../shared/components/EntityMultiSelect';
import { contractFormSchema } from './documentCreation.schemas';
import React from 'react';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import CreditRowsFieldArray from './CreditRowsFieldArray';
import TranchesFieldArray from './TranchesFieldArray';
import ClientCustomFieldsSection from '../../../clients/components/ClientCustomFieldsSection';
import { archiveDocumentsCustomField, createDocumentsCustomField, listDocumentsCustomFields } from '../../services/documents-custom-fields.service';

const CONTRACT_CUSTOM_FIELDS_API = { list: listDocumentsCustomFields, create: createDocumentsCustomField, archive: archiveDocumentsCustomField };

export default function CreateContractForm({
  clients,
  clientsUnavailable = false,
  form,
  totals,
  isSubmitting,
  onFieldChange,
  onCreditRowChange,
  onAddCreditRow,
  onRemoveCreditRow,
  onTrancheChange,
  onAddTranche,
  onRemoveTranche,
  onSubmit,
  enableCustomFields = false,
  renderActions = true,
}) {
  return (
    <div className="space-y-6 py-4">
      <div className="bg-[var(--primary-soft)] p-4 rounded-lg border border-[var(--border-strong)]">
        <h3 className="font-semibold text-[var(--primary)] mb-3">Dati Contratto</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="contract-date">{contractFormSchema.field('formData.dataContratto').label} *</Label>
            <Input id="contract-date" required type="date" value={form.dataContratto} onChange={(event) => onFieldChange('dataContratto', event.target.value)} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="contract-place">{contractFormSchema.field('formData.luogoContratto').label} *</Label>
            <Input id="contract-place" required value={form.luogoContratto} onChange={(event) => onFieldChange('luogoContratto', event.target.value)} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="contract-client">Cliente *</Label>
            <EntityMultiSelect label="Clienti" options={clients} value={selectedEntityIds(form, 'client')} required
              onChange={(ids) => onFieldChange('clientIds', ids)} />
            {clientsUnavailable ? (
              <p role="alert" className="mt-1 text-xs wt-inline-error">
                Metadata clienti non disponibili. Generazione contratto temporaneamente limitata.
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="contract-signer-role">{contractFormSchema.field('formData.firmatarioRuolo').label}</Label>
            <Input id="contract-signer-role" value={form.firmatarioRuolo} onChange={(event) => onFieldChange('firmatarioRuolo', event.target.value)} className="mt-1" />
          </div>
        </div>
      </div>

      <CreditRowsFieldArray
        rows={form.righeCrediti}
        onAdd={onAddCreditRow}
        onRemove={onRemoveCreditRow}
        onChange={onCreditRowChange}
      />

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-[var(--surface-raised)] p-4 rounded-lg border border-[var(--border)]">
          <p className="text-xs text-[var(--text-secondary)]">Totale Nominale</p>
          <p className="text-lg font-bold text-[var(--text-primary)]">EUR {totals.totalNominale.toFixed(2)}</p>
        </div>
        <div className="bg-[var(--surface-raised)] p-4 rounded-lg border border-[var(--border)]">
          <p className="text-xs text-[var(--text-secondary)]">Totale Prezzo Acquisto</p>
          <p className="text-lg font-bold text-[var(--text-primary)]">EUR {totals.totalPrezzoAcquisto.toFixed(2)}</p>
        </div>
      </div>

      <TranchesFieldArray
        rows={form.tranche}
        onAdd={onAddTranche}
        onRemove={onRemoveTranche}
        onChange={onTrancheChange}
      />

      <div className="bg-[var(--surface-raised)] p-4 rounded-lg border border-[var(--border)] grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="contract-iban">{contractFormSchema.field('formData.iban').label}</Label>
          <Input id="contract-iban" value={form.iban} onChange={(event) => onFieldChange('iban', event.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="contract-bank-name">{contractFormSchema.field('formData.nomeBanca').label}</Label>
          <Input id="contract-bank-name" value={form.nomeBanca} onChange={(event) => onFieldChange('nomeBanca', event.target.value)} className="mt-1" />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Checkbox id="contract-ownership" checked={form.dichiarazioneTitolarita} onCheckedChange={(value) => onFieldChange('dichiarazioneTitolarita', Boolean(value))} />
          <label htmlFor="contract-ownership" className="text-sm text-[var(--text-primary)]">Dichiaro la titolarita dei crediti</label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="contract-no-liens" checked={form.dichiarazioneAssenzaGravami} onCheckedChange={(value) => onFieldChange('dichiarazioneAssenzaGravami', Boolean(value))} />
          <label htmlFor="contract-no-liens" className="text-sm text-[var(--text-primary)]">Dichiaro assenza di gravami</label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="contract-acceptance" checked={form.accettazione1341_1} onCheckedChange={(value) => onFieldChange('accettazione1341_1', Boolean(value))} />
          <label htmlFor="contract-acceptance" className="text-sm text-[var(--text-primary)]">Accetto clausole ex art. 1341</label>
        </div>
      </div>

      {enableCustomFields ? (
        <ClientCustomFieldsSection
          formKey="documents.contract.create"
          values={form.customFields || {}}
          onChange={(customFields) => onFieldChange('customFields', customFields)}
          api={CONTRACT_CUSTOM_FIELDS_API}
        />
      ) : null}

      {renderActions ? <div className="flex gap-2">
        <Button
          className="flex-1 bg-[var(--primary)] hover:bg-[var(--primary)] text-[var(--on-primary)]"
          disabled={isSubmitting}
          onClick={onSubmit}
        >
          {isSubmitting ? 'Generazione in corso...' : 'Genera Contratto'}
        </Button>
      </div> : null}
    </div>
  );
}
